import { useMemo, useState } from "react";

import { useConfirmationDialogContext } from "@shared";

import { AppointmentExchangeResponse, AppointmentExchangeStatus } from "@/api";
import { useCounselors } from "@/common/components/counselor-select/hooks/useCounselors";
import { useCurrentUserContext } from "@/common/contexts/current-user-context/CurrentUserContext";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { useAppointmentLabels } from "@/common/hooks/useAppointmentLabels";
import { ExchangeView } from "@/pages/exchange/Exchange.types";
import { formatExchangeAppointment, resolveExchangeCounselorLabel } from "@/pages/exchange/Exchange.utils";
import { useTranslation } from "@/translations";

import { useExchangeOfferMutations } from "./useExchangeOfferMutations";
import { useExchangeQuery } from "./useExchangeQuery";
import { useExchangeRequestMutations } from "./useExchangeRequestMutations";

/**
 * Page logic for the Tauschbörse: loads the requests, derives the rows of the active view — the user's
 * own requests (open and erledigt), or the colleagues' open requests narrowed by the Berater:in filter —
 * and owns the two flows: the offer flow on a colleague's row (open the offer dialog, confirm a
 * withdrawal) and the decision flow on an own row (open the offers overlay, confirm a deletion).
 */
export function useExchange() {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const labels = useAppointmentLabels();
  const { showConfirmationDialog } = useConfirmationDialogContext();
  const { counselors } = useCounselors();
  const { currentUser } = useCurrentUserContext();
  const { data, isLoading } = useExchangeQuery();
  const offerMutations = useExchangeOfferMutations();
  const requestMutations = useExchangeRequestMutations();
  const [view, setView] = useState<ExchangeView>("others");
  const [counselorId, setCounselorId] = useState<string | null>(null);
  const [isNewRequestOpen, setIsNewRequestOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [offerTarget, setOfferTarget] = useState<AppointmentExchangeResponse | null>(null);
  const [offersExchangeId, setOffersExchangeId] = useState<string | null>(null);

  const rows = useMemo(() => {
    const exchanges = data ?? [];
    const ownCounselorId = currentUser.counselorId;
    const isOwn = (exchange: AppointmentExchangeResponse) =>
      ownCounselorId != null && exchange.createdByCounselorId === ownCounselorId;

    if (view === "mine") {
      return exchanges.filter(isOwn);
    }
    return exchanges.filter(
      (exchange) =>
        !isOwn(exchange) &&
        exchange.status === AppointmentExchangeStatus.Open &&
        (counselorId === null || exchange.createdByCounselorId === counselorId)
    );
  }, [data, view, counselorId, currentUser.counselorId]);

  // Held by id, not as a row snapshot: the overlay re-renders from the refetched list after an accept
  // (the footer drops "Anfrage löschen") and closes by itself once the row is gone.
  const offersTarget = useMemo(
    () => (offersExchangeId === null ? null : (data?.find((exchange) => exchange.id === offersExchangeId) ?? null)),
    [data, offersExchangeId]
  );
  const closeOffers = () => setOffersExchangeId(null);

  const requestWithdrawOffer = (row: AppointmentExchangeResponse) => {
    const offerId = row.ownPendingOfferId;
    if (offerId === null) {
      return;
    }
    showConfirmationDialog({
      dialogTitleText: t("exchange.withdrawOfferDialog.title"),
      dialogText: t("exchange.withdrawOfferDialog.text", {
        counselor: resolveExchangeCounselorLabel(row, counselors),
        appointment: formatExchangeAppointment(row, locales[language], t),
        purpose: labels.purposeLabelOf(row.purposeKey),
      }),
      agreeText: t("exchange.withdrawOfferDialog.confirm"),
      agreeColor: "error",
      onClose: (confirmed) => {
        if (confirmed) {
          offerMutations.withdrawOffer.mutate({ exchangeId: row.id, offerId });
        }
      },
    });
  };

  const requestDeleteExchange = (row: AppointmentExchangeResponse) => {
    showConfirmationDialog({
      dialogTitleText: t("exchange.deleteRequestDialog.title"),
      dialogText: t("exchange.deleteRequestDialog.text", {
        appointment: formatExchangeAppointment(row, locales[language], t),
        purpose: labels.purposeLabelOf(row.purposeKey),
      }),
      agreeText: t("exchange.deleteRequestDialog.confirm"),
      agreeColor: "error",
      onClose: (confirmed) => {
        if (confirmed) {
          requestMutations.deleteRequest.mutate(row.id, { onSuccess: closeOffers });
        }
      },
    });
  };

  return {
    view,
    setView,
    counselorId,
    setCounselorId,
    rows,
    isLoading,
    isBusy: offerMutations.isBusy || requestMutations.isBusy,
    paginationResetKey: `${view}|${counselorId ?? ""}`,
    isNewRequestOpen,
    openNewRequest: () => setIsNewRequestOpen(true),
    closeNewRequest: () => setIsNewRequestOpen(false),
    isHistoryOpen,
    openHistory: () => setIsHistoryOpen(true),
    closeHistory: () => setIsHistoryOpen(false),
    offerTarget,
    openMakeOffer: setOfferTarget,
    closeMakeOffer: () => setOfferTarget(null),
    requestWithdrawOffer,
    offersTarget,
    openOffers: (row: AppointmentExchangeResponse) => setOffersExchangeId(row.id),
    closeOffers,
    requestDeleteExchange,
    isDeletingRequest: requestMutations.deleteRequest.isPending,
  };
}
